<script setup>
import { onMounted, ref, defineProps, defineEmits, defineExpose, watch, nextTick, onBeforeUnmount, computed } from 'vue';
import { useIcons } from '@/composables/icons.js';
import { useIconSelect } from '@/composables/useIconSelect.js';

const { User } = useIcons();
const { getAllIcons } = useIconSelect();

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: ''
  },
  options: {
    type: Array,
  },
  type: {
    type: String,
    default: 'text'
  },
  id: {
    type: String,
    required: true
  },
  placeholder: {
    type: [String, Object],
    default: ''
  },
  required: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: false
  },
  showProfilePicture: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue']);
const input = ref(null);
const isOpen = ref(false);
const containerRef = ref(null);
const searchQuery = ref('');
const icons = ref(getAllIcons());

const displayValue = computed(() => {
  const selectedOption = icons.value.find(icon => icon.class === props.modelValue);
  
  if (selectedOption) {
    return selectedOption.label;
  }
  
  const placeholderValue = typeof props.placeholder === 'object' ? props.placeholder.label : props.placeholder;
  return placeholderValue;
});

const filteredOptions = computed(() => {
  if (!searchQuery.value) return icons.value;
  const query = searchQuery.value.toLowerCase();
  return icons.value.filter(option => 
    option.label.toLowerCase().includes(query)
  );
});

const selectedOptionWithImage = computed(() => {
  return icons.value.find(icon => icon.class === props.modelValue);
});

const handleSelect = (option) => {
  if (props.disabled) return;
  
  const selectedValue = option.class !== undefined ? option.class : option.value;
  
  if (props.modelValue === selectedValue) {
    emit('update:modelValue', '');
  } else {
    emit('update:modelValue', selectedValue);
  }
  
};

const toggleDropdown = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    searchQuery.value = '';
  }
};

const updateDropdownPosition = () => {
  if (!isOpen.value || !containerRef.value) return;
  
  const dropdown = containerRef.value.querySelector('.select__dropdown');
  if (dropdown) {
    dropdown.style.width = '100%';
  }
};

watch(isOpen, (newValue) => {
  if (newValue) {
    nextTick(() => {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition);
      window.addEventListener('resize', updateDropdownPosition);
    });
  } else {
    window.removeEventListener('scroll', updateDropdownPosition);
    window.removeEventListener('resize', updateDropdownPosition);
  }
});

onMounted(() => {
  const handleClickOutside = (event) => {
    if (containerRef.value && !containerRef.value.contains(event.target)) {
      isOpen.value = false;
    }
  };
  document.addEventListener('click', handleClickOutside);
  onBeforeUnmount(() => {
    document.removeEventListener('click', handleClickOutside);
    window.removeEventListener('scroll', updateDropdownPosition);
    window.removeEventListener('resize', updateDropdownPosition);
  });
});



defineExpose({ focus: () => input.value?.focus() });
</script>

<template>
  <div class="select" ref="containerRef">
    <div 
      class="select__select"
      :class="{ 
        'select__select--open': isOpen,
        'select__select--has-value': modelValue,
        'select__select--disabled': disabled
      }"
      @click="toggleDropdown"
    >
      <div class="select__value">
        <div v-if="showProfilePicture && selectedOptionWithImage" class="select__value-with-avatar">
          <div class="select__value-avatar">
            <img v-if="selectedOptionWithImage.profilePicture" :src="selectedOptionWithImage.profilePicture" alt="" class="select__value-avatar-img">
            <User v-else class="w-full h-full p-1 text-white" />
          </div>
          <span class="select__value-label truncate">{{ displayValue }}</span>
        </div>
        <span v-else :class="{'text-white/50': !modelValue}" class="truncate flex items-center gap-2">
          <i v-if="selectedOptionWithImage" :class="[selectedOptionWithImage.class, 'text-lg']"></i>
          <i v-else class="uil uil-smile text-lg"></i>
        </span>
      </div>
      <div class="select__actions">
        <div class="select__caret">
          <svg 
            class="select__caret-icon"
            :class="{ 'select__caret-icon--open': isOpen }"
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </div>
      </div>
    </div>

    <div v-show="isOpen && !disabled" class="select__dropdown">
      <input
        type="text"
        class="select__search"
        v-model="searchQuery"
        placeholder="Search..."
        @click.stop
      >
      <div class="select__options-grid">
        <template v-if="filteredOptions?.length">
          <button
            v-for="option in filteredOptions"
            :key="option?.value ?? option?.name"
            type="button"
            class="select__option-grid"
            :class="{ 'select__option-grid--selected': (option.class !== undefined ? option.class : option.value) === modelValue }"
            @click="handleSelect(option)"
          >
            <i v-if="option.class" :class="option.class"></i>
          </button>
        </template>
        <div v-else class="select__no-options text-xs whitespace-nowrap text-white">
          No options available
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.select {
  @apply relative w-[90px];
}

.select__select {
  @apply flex items-center justify-between w-full px-4 py-2.5 rounded-[13px] h-11
    bg-echoes-background-black
      text-white
      border border-echoes-border-gray
    transition-all duration-200 ease-in-out cursor-pointer
    focus:outline-none  focus:border-echoes-primary;

  &:not(.select__select--open):hover {
    @apply bg-echoes-background-black
    border-white/20
    text-white
    ;
  }
}

.select__select--open {
  @apply border-echoes-primary
    bg-echoes-background-black;
}

.select__select--disabled {
  @apply opacity-50 cursor-not-allowed bg-echoes-background-black border-echoes-border-gray;

  &:hover {
    @apply bg-echoes-background-black border-echoes-border-gray;
  }
}

.select__value {
  @apply text-sm flex-1 pr-2 overflow-hidden;
}

.select__value-with-avatar {
  @apply flex items-center w-full overflow-hidden;
}

.select__value-avatar {
  @apply w-6 h-6 rounded-full overflow-hidden bg-echoes-primary mr-2 flex-shrink-0;
}

.select__value-avatar-img {
  @apply w-full h-full object-cover;
}

.select__value-label {
  @apply flex-1 overflow-hidden;
}

.select__placeholder {
  @apply text-white/50 hover:text-white/80 text-sm flex items-center;
}

.select__caret {
  @apply flex items-center ml-2 flex-shrink-0;
}

.select__caret-icon {
  @apply w-5 h-5 text-white/80 transition-transform duration-200;
}

.select__caret-icon--open {
  @apply transform rotate-180;
}

.select__dropdown {
  @apply absolute z-[9999] mt-1
    bg-echoes-background-black 
    border border-echoes-border-gray
    rounded-[13px] shadow-sm
    overflow-hidden
    min-w-[220px]
    left-0;
  
}

.select__search {
  @apply w-full px-4 py-2 text-sm
    bg-transparent
    text-white 
    border-b border-echoes-border-gray
    placeholder-white/50 hover:placeholder-white/80;

  &:focus {
    @apply outline-none;
  }
}

.select__options {
  @apply max-h-[160px] overflow-y-auto;
}

.select__option {
  @apply w-full px-4 py-2.5 text-left text-sm h-11
    text-white 
    transition-colors duration-150 ease-in-out
    flex items-center overflow-hidden;

  &:hover {
    @apply bg-echoes-background-gray/30;
  }
}

.select__option-avatar {
  @apply w-6 h-6 rounded-full overflow-hidden bg-echoes-primary mr-2 flex-shrink-0;
}

.select__option-avatar-img {
  @apply w-full h-full object-cover;
}

.select__option-label {
  @apply flex-1 overflow-hidden;
}

.select__actions {
  @apply flex items-center gap-1 flex-shrink-0;
}

.select__clear-button {
  @apply rounded-full hover:bg-echoes-background-gray/50
    transition-colors duration-150 p-1;
}

.select__clear-icon {
  @apply w-4 h-4 text-white/80;
}

.select__options-grid {
  @apply max-h-[160px] overflow-y-auto p-2;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(40px, 1fr));
  gap: 0.5rem;
}

.select__option-grid {
  @apply w-full aspect-square flex items-center justify-center
    text-white 
    transition-colors duration-150 ease-in-out
    rounded-lg border border-transparent;

  &:hover {
    @apply bg-echoes-background-gray/30 border-echoes-border-gray;
  }

  i {
    @apply text-lg;
  }
}

.select__option-grid--selected {
  @apply bg-echoes-primary/20 border-echoes-primary;

  &:hover {
    @apply bg-echoes-primary/20 border-echoes-primary;
  }
}
</style>

